import { Project } from "../types";

// This service handles interactions with the YouTube Data API v3
// API Key is injected via vite.config.ts define
const API_KEY = process.env.API_KEY;
const YOUTUBE_API_BASE = 'https://www.googleapis.com/youtube/v3';

interface VideoStats {
  id: string;
  snippet: {
    title: string;
    description: string;
    thumbnails: {
      maxres?: { url: string };
      standard?: { url: string };
      high: { url: string };
      medium: { url: string };
    };
  };
  statistics: {
    viewCount: string;
    likeCount: string;
    commentCount: string;
  };
}

interface YouTubeApiResponse {
  items: VideoStats[];
  error?: {
    code: number;
    message: string;
    errors?: Array<{ domain: string; reason: string; message: string }>;
  };
}

interface ChannelDetailsResponse {
  items: {
    contentDetails: {
      relatedPlaylists: {
        uploads: string;
      };
    };
  }[];
}

interface PlaylistItemsResponse {
  items: {
    snippet: {
      title: string;
      description: string;
      publishedAt: string;
      resourceId: {
        videoId: string;
      };
      thumbnails: {
        maxres?: { url: string };
        standard?: { url: string };
        high: { url: string };
        medium: { url: string };
      };
    };
  }[];
}

// Helper to check cache validity (15 mins for more frequent updates)
const isCacheValid = (timestamp: string | null) => {
  if (!timestamp) return false;
  return (Date.now() - parseInt(timestamp)) < 1000 * 60 * 15;
};

/**
 * DEBUG FUNCTION: explicitly tests the connection and returns a human-readable status.
 */
export const testYouTubeConnection = async (): Promise<{ success: boolean; message: string }> => {
  try {
    if (!API_KEY) {
         return { success: false, message: 'Missing API_KEY (Mapped from VITE_API_KEY)' };
    }

    // Try to fetch a known public video (YouTube Developers channel video) to test key
    const TEST_VIDEO_ID = 'Ks-_Mh1QhMc'; 
    const response = await fetch(`${YOUTUBE_API_BASE}/videos?part=statistics&id=${TEST_VIDEO_ID}&key=${API_KEY}`);
    const data: YouTubeApiResponse = await response.json();

    if (!response.ok) {
      const errorMsg = data.error?.message || 'Unknown Error';
      
      if (errorMsg.includes('API key not valid')) {
         return { 
             success: false, 
             message: `Invalid API Key. If the key is correct, check "Application Restrictions" in Google Cloud. If you set it to "Websites", ensure this current URL is in the allowed list.` 
         };
      }
      if (data.error?.errors?.[0]?.reason === 'accessNotConfigured') {
         return { success: false, message: 'YouTube Data API v3 is NOT enabled. Please enable it in Google Cloud Console Library.' };
      }
      return { success: false, message: `API Error: ${errorMsg}` };
    }

    return { success: true, message: 'Connection Successful! API Key is valid and Data API is enabled.' };
  } catch (error: any) {
    return { success: false, message: `Network Error: ${error.message}` };
  }
};

/**
 * Fetches statistics AND snippet (metadata) for a list of YouTube video IDs.
 */
export const fetchYouTubeStats = async (videoIds: string[]): Promise<Record<string, Partial<Project>>> => {
  if (!API_KEY || videoIds.length === 0) {
    return {};
  }

  const uniqueIds = Array.from(new Set(videoIds.filter(Boolean)));
  const results: Record<string, Partial<Project>> = {};
  const CHUNK_SIZE = 50;

  for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
    const chunk = uniqueIds.slice(i, i + CHUNK_SIZE);
    const idsString = chunk.map(id => encodeURIComponent(id)).join(',');

    try {
      const fetchUrl = `${YOUTUBE_API_BASE}/videos?part=statistics,snippet&id=${idsString}&key=${API_KEY}`;
      const response = await fetch(fetchUrl);
      const data: YouTubeApiResponse = await response.json();

      if (!response.ok) {
        console.error(`YouTube API Error [${response.status}]:`, data.error?.message);
        continue;
      }
      
      if (data.items) {
        data.items.forEach((item) => {
          const thumb = item.snippet?.thumbnails?.maxres?.url || 
                        item.snippet?.thumbnails?.standard?.url || 
                        item.snippet?.thumbnails?.high?.url || 
                        item.snippet?.thumbnails?.medium?.url;

          results[item.id] = {
             views: parseInt(item.statistics.viewCount || '0', 10),
             title: item.snippet?.title,
             description: item.snippet?.description,
             thumbnailUrl: thumb
          };
        });
      }
    } catch (error) {
      console.error('Failed to fetch YouTube stats:', error);
    }
  }

  return results;
};

/**
 * Fetches the latest 50 videos from the channel to perform matching.
 */
export const fetchLatestChannelVideos = async (channelIdInput: string, force = false): Promise<Partial<Project>[]> => {
  if (!API_KEY || !channelIdInput || channelIdInput.includes('YOUR_CHANNEL_ID')) {
    return [];
  }

  const channelId = channelIdInput.trim();
  const cacheKey = `yt_videos_${channelId}`;
  const cacheTimeKey = `yt_videos_time_${channelId}`;
  
  if (!force) {
      const cachedData = localStorage.getItem(cacheKey);
      const cachedTime = localStorage.getItem(cacheTimeKey);

      if (cachedData && isCacheValid(cachedTime)) {
        return JSON.parse(cachedData);
      }
  }

  try {
    let queryParam = `id=${encodeURIComponent(channelId)}`;
    if (channelId.startsWith('@') || !channelId.startsWith('UC')) {
        queryParam = `forHandle=${encodeURIComponent(channelId)}`;
    }

    const channelRes = await fetch(
      `${YOUTUBE_API_BASE}/channels?part=contentDetails&${queryParam}&key=${API_KEY}`
    );
    
    if (!channelRes.ok) return [];

    const channelData: ChannelDetailsResponse = await channelRes.json();
    
    if (!channelData.items || channelData.items.length === 0) return [];

    const uploadsPlaylistId = channelData.items[0].contentDetails?.relatedPlaylists?.uploads;
    
    if (!uploadsPlaylistId) return [];

    // Fetches up to 50 videos to ensure we cover the 27 days
    const playlistRes = await fetch(
      `${YOUTUBE_API_BASE}/playlistItems?part=snippet&playlistId=${uploadsPlaylistId}&maxResults=50&key=${API_KEY}`
    );
    
    if (!playlistRes.ok) return [];

    const playlistData: PlaylistItemsResponse = await playlistRes.json();

    if (!playlistData.items) return [];

    const projects: Partial<Project>[] = playlistData.items.map(item => {
       const thumb = item.snippet.thumbnails.maxres?.url || 
                     item.snippet.thumbnails.standard?.url || 
                     item.snippet.thumbnails.high?.url || 
                     item.snippet.thumbnails.medium?.url;

       return {
         title: item.snippet.title,
         description: item.snippet.description.split('\n')[0] || 'Watch this video on YouTube.',
         createdAt: new Date(item.snippet.publishedAt).getTime(),
         youtubeVideoId: item.snippet.resourceId.videoId,
         thumbnailUrl: thumb,
         url: `https://www.youtube.com/watch?v=${item.snippet.resourceId.videoId}`,
         tags: ['YouTube'],
         category: 'Web App'
       };
    });

    localStorage.setItem(cacheKey, JSON.stringify(projects));
    localStorage.setItem(cacheTimeKey, Date.now().toString());

    return projects;

  } catch (error) {
    console.error("Error fetching channel videos:", error);
    return [];
  }
};

export const extractYouTubeId = (url: string): string | null => {
  if (!url) return null;
  const cleanUrl = url.trim();
  try {
    const urlObj = new URL(cleanUrl);
    // youtu.be/ID
    if (urlObj.hostname === 'youtu.be') {
        return urlObj.pathname.slice(1);
    }
    // youtube.com/watch?v=ID or /shorts/ID
    if (urlObj.hostname.includes('youtube.com')) {
      if (urlObj.pathname.startsWith('/shorts/')) return urlObj.pathname.split('/')[2];
      if (urlObj.searchParams.has('v')) return urlObj.searchParams.get('v');
    }
  } catch (e) {
      // Ignore URL parsing errors and try regex
  }
  
  // Fallback Regex for partial URLs or odd formats
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = cleanUrl.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};